import { shareOrDownloadBlob } from './shareOrDownloadBlob.js';
import {
  A4_PORTRAIT_HEIGHT_MM,
  A4_PORTRAIT_WIDTH_MM,
  FullPageExportStageError,
  type FullPageExportDelivery,
  type FullPageExportStage,
} from './types.js';

function asCanvasList(
  canvasOrCanvases: HTMLCanvasElement | readonly HTMLCanvasElement[],
): HTMLCanvasElement[] {
  return Array.isArray(canvasOrCanvases) ? [...canvasOrCanvases] : [canvasOrCanvases as HTMLCanvasElement];
}

function sliceCanvasBand(
  source: HTMLCanvasElement,
  sourceY: number,
  bandHeight: number,
): HTMLCanvasElement {
  const slice = document.createElement('canvas');
  slice.width = source.width;
  slice.height = bandHeight;
  const ctx = slice.getContext('2d');
  if (ctx == null) {
    throw new Error('Full-page PDF export failed: 2D canvas context is unavailable. Retry, or download as PNG.');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, slice.width, slice.height);
  ctx.drawImage(source, 0, sourceY, slice.width, bandHeight, 0, 0, slice.width, bandHeight);
  return slice;
}

function rethrowAsStageError(stage: FullPageExportStage, err: unknown, fallbackMessage: string): never {
  if (err instanceof FullPageExportStageError) {
    throw err;
  }
  const message = err instanceof Error && err.message.trim().length > 0 ? err.message : fallbackMessage;
  throw new FullPageExportStageError(stage, message, { cause: err });
}

/**
 * A4 portrait multi-page raster PDF. Slices captured canvas(es); never jsPDF `.text()`, never window.print().
 * Multiple capture bands are paginated in order without stitching into one over-budget canvas.
 * Encode failures are tagged `encode-pdf`; share/download failures are tagged `share-download`.
 */
export async function exportElementPdf(
  canvasOrCanvases: HTMLCanvasElement | readonly HTMLCanvasElement[],
  filenameBase: string,
  delivery: FullPageExportDelivery = 'download',
): Promise<void> {
  let pdfBlob: Blob;
  try {
    const canvases = asCanvasList(canvasOrCanvases);
    if (canvases.length === 0) {
      throw new Error('Full-page PDF export failed: capture produced no canvases. Retry, or download as PNG.');
    }

    const jsPDF = await import('jspdf');
    const pdf = new jsPDF.default('portrait', 'mm', 'a4');
    const pageWidthMm = A4_PORTRAIT_WIDTH_MM;
    const pageHeightMm = A4_PORTRAIT_HEIGHT_MM;

    let pageIndex = 0;
    for (const canvas of canvases) {
      const sourceWidth = Math.max(1, canvas.width);
      const pageHeightPx = Math.max(1, Math.floor((pageHeightMm / pageWidthMm) * sourceWidth));

      let sourceY = 0;
      while (sourceY < canvas.height) {
        if (pageIndex > 0) {
          pdf.addPage();
        }
        const bandHeight = Math.min(pageHeightPx, canvas.height - sourceY);
        const band = sliceCanvasBand(canvas, sourceY, bandHeight);
        const destHeightMm = (bandHeight / sourceWidth) * pageWidthMm;
        pdf.addImage(band.toDataURL('image/png'), 'PNG', 0, 0, pageWidthMm, destHeightMm);
        sourceY += bandHeight;
        pageIndex += 1;
      }
    }

    if (pageIndex === 0) {
      const fallback = canvases[0];
      if (fallback != null) {
        pdf.addImage(fallback.toDataURL('image/png'), 'PNG', 0, 0, pageWidthMm, pageHeightMm);
      }
    }

    pdfBlob = pdf.output('blob') as Blob;
  } catch (err) {
    rethrowAsStageError(
      'encode-pdf',
      err,
      'Full-page PDF export failed during encoding. Retry, or download as PNG.',
    );
  }

  try {
    await shareOrDownloadBlob(
      pdfBlob,
      {
        filename: `${filenameBase}.pdf`,
        mimeType: 'application/pdf',
      },
      delivery,
    );
  } catch (err) {
    rethrowAsStageError(
      'share-download',
      err,
      'Full-page PDF export failed during share or download. Retry the export.',
    );
  }
}
